<!--
  SendPendingArtworks -- the Decorations page's "Send pending artworks"
  button (ADR 0036, Jake decision Q20, 2026-11-03).

  WHAT IT SENDS. The artworks on this store whose pipeline_state is still
  `uploaded` (never sent), read from the decoration rollup. For each product
  such an artwork is placed on, one POST
  /api/campaign_products/<id>/send_pending_artworks {artwork_ids, client_ids}.
  client_ids is the store's own client (stores.client_id): the page cannot
  request approval from anyone else, so a store with no client cannot send.

  IT ASKS FIRST. Sending creates digitizer jobs and client approval requests;
  once Artistic Punch is live that costs real money, so the press opens a
  confirmation and nothing is sent until the user confirms. It makes no fee
  claim: AP cost rides in the subscription at MVP and nothing here bills the
  distributor (Jake, via tf7-super, 2026-11-03).

  IT BYPASSES NOTHING. Capability (distributor.digitization.send) hides the
  control; the endpoint re-enforces it, the dry-run switch, the method gates
  and the DTF resolution refusal. DTF art is never sent by the batch (it goes
  through the single-artwork dialog); the endpoint reports it as
  `dtf_not_sent` and this component says so.

  RESULT. Approvals requested, then each artwork the endpoint did not act on
  with the reason in words. A benign repeat (the same artwork met again on a
  second product, already sent / already covered by the first call) is not
  reported twice.

  The host owns the rollup: this emits `sent` after the calls finish (even
  partly failed) so the host re-reads it.
-->
<template>
  <div class="spa" data-testid="send-pending-artworks">
    <button
      v-if="canSend"
      type="button"
      class="spa__btn"
      data-testid="decorations-send-pending-btn"
      :disabled="!!blockedReason || sending"
      @click="open = true"
    >
      <span class="tf-icon">send</span>
      Send pending artworks ({{ pendingArtworkIds.length }})
    </button>
    <p
      v-if="canSend && blockedReason"
      class="spa__note"
      data-testid="send-pending-note"
    >
      {{ blockedReason }}
    </p>

    <div
      v-if="open"
      class="spa-backdrop"
      @click.self="cancel"
    >
      <div
        class="spa-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spa-title"
        data-testid="send-pending-dialog"
      >
        <h3 id="spa-title" class="spa-dialog__title">Send pending artworks</h3>
        <p class="spa-dialog__body" data-testid="send-pending-dialog-body">
          This will send {{ pendingArtworkIds.length }}
          artwork{{ pendingArtworkIds.length === 1 ? "" : "s" }} on
          {{ productCount }} product{{ productCount === 1 ? "" : "s" }} to the
          digitizer, and ask the store's client to approve
          {{ pendingArtworkIds.length === 1 ? "it" : "them" }}. DTF artwork is
          not sent from here.
        </p>
        <div class="spa-dialog__actions">
          <button
            type="button"
            class="spa__btn spa__btn--ghost"
            data-testid="send-pending-cancel"
            @click="cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="spa__btn"
            data-testid="send-pending-confirm"
            @click="confirm"
          >
            Send
          </button>
        </div>
      </div>
    </div>

    <p
      v-if="error"
      class="spa__error"
      role="alert"
      data-testid="send-pending-error"
    >
      {{ error }}
    </p>

    <div
      v-if="result"
      class="spa__result"
      role="status"
      data-testid="send-pending-result"
    >
      <p class="spa__result-head">
        {{ result.sentCount }}
        client approval{{ result.sentCount === 1 ? "" : "s" }} requested.
      </p>
      <ul v-if="result.lines.length" class="spa__result-list">
        <li v-for="(line, i) in result.lines" :key="i">{{ line }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import { SUPPRESS_ERROR_TOAST } from "@/interceptors/authResponseInterceptor";
import { formatArtworkName } from "@/utils/formatArtworkName";

const SEND_CAP = "distributor.digitization.send";

const DTF_NOT_SENT_HERE = "is DTF art, which is not sent from here";

// What a skip means, in the user's words. An unknown code falls back to the
// code itself with underscores turned into spaces (never silent).
const SKIP_MESSAGES = {
  not_found_or_cross_tenant: "was not found",
  no_current_version: "has no current version",
  already_sent: "was already sent",
  not_uploaded_state: "is waiting on a re-send, which is done from its own page",
  no_decorations_to_approve: "has no sizes to approve",
  duplicate_approval_record: "already has an approval request",
  no_live_decoration_on_current_version:
    "has no size on its current version to digitize",
  already_covered_by_live_job: "is already being digitized",
  already_digitized: "is already digitized",
  // DTF is excluded from the POST, so these are nearly unreachable; every
  // DTF-send refusal reads the same plain way and mentions no fee.
  dtf_needs_acknowledged_send: DTF_NOT_SENT_HERE,
  art_cleanup_ack_required: DTF_NOT_SENT_HERE,
  art_cleanup_fee_changed: DTF_NOT_SENT_HERE,
  art_cleanup_daily_cap_reached: DTF_NOT_SENT_HERE,
  dtf_art_cleanup_disabled: "is DTF art, and sending DTF art is not available right now",
  art_cleanup_refused: "is DTF art that could not be sent from here",
};

// A later product's call repeats these for an artwork the first call already
// handled; they say nothing new.
const BENIGN_REPEATS = new Set([
  "already_sent",
  "duplicate_approval_record",
  "already_covered_by_live_job",
]);

function reasonText(reason) {
  if (SKIP_MESSAGES[reason]) return SKIP_MESSAGES[reason];
  // The endpoint appends the server's own text after the code; keep that out
  // of the sentence.
  if (String(reason).startsWith("route_to_digitizer_failed")) {
    return "could not be sent to the digitizer";
  }
  return String(reason || "was not sent").replace(/_/g, " ");
}

export default {
  name: "SendPendingArtworks",

  props: {
    /** The rollup's decorations (archived already excluded by the host). */
    decorations: { type: Array, default: () => [] },
    /** stores.client_id: the one client approvals are requested from. */
    clientId: { type: [Number, String], default: null },
  },

  emits: ["sent"],

  data() {
    return { open: false, sending: false, error: null, result: null };
  },

  computed: {
    // Fail closed: no injected capability list means no control.
    canSend() {
      const caps = this.$root?.$?.appContext?.config?.globalProperties
        ?.$distributorCapabilities;
      return Array.isArray(caps) && caps.includes(SEND_CAP);
    },

    // artwork_id -> [campaign_product_id, ...] for every artwork still
    // `uploaded`, in the rollup's order.
    pendingByArtwork() {
      const map = new Map();
      for (const d of this.decorations) {
        if (d.pipeline_state !== "uploaded" || d.artwork_id == null) continue;
        // A plain Send skips DTF (it goes out only via the art-cleanup
        // action), so a DTF decoration neither counts nor is posted. A mixed
        // artwork still counts for its non-DTF placements.
        if (d.method?.slug === "dtf") continue;
        const cps = map.get(d.artwork_id) || [];
        for (const p of d.placements || []) {
          if (p.campaign_product_id != null && !cps.includes(p.campaign_product_id)) {
            cps.push(p.campaign_product_id);
          }
        }
        map.set(d.artwork_id, cps);
      }
      return map;
    },

    pendingArtworkIds() {
      return [...this.pendingByArtwork.keys()];
    },

    // Uploaded artworks held back only because every placement is DTF.
    dtfOnlyWaiting() {
      return this.decorations.some(
        (d) =>
          d.pipeline_state === "uploaded" &&
          d.artwork_id != null &&
          d.method?.slug === "dtf",
      );
    },

    productCount() {
      return this.batches().size;
    },

    blockedReason() {
      if (this.pendingArtworkIds.length === 0) {
        return this.dtfOnlyWaiting
          ? "The only artworks waiting are DTF. DTF artwork is not sent from here."
          : "No artworks are waiting to be sent.";
      }
      if (this.clientId == null) {
        return "Assign a client to this store first: approvals are requested from the store's client.";
      }
      return null;
    },

    nameByProduct() {
      const names = new Map();
      for (const d of this.decorations) {
        for (const p of d.placements || []) {
          if (p.campaign_product_id != null && !names.has(p.campaign_product_id)) {
            names.set(p.campaign_product_id, p.product_name || `Product ${p.campaign_product_id}`);
          }
        }
      }
      return names;
    },

    nameByArtwork() {
      const names = new Map();
      for (const d of this.decorations) {
        if (d.artwork_id == null || names.has(d.artwork_id)) continue;
        const n = formatArtworkName(d.artwork_original_filename || d.artwork_filename);
        names.set(d.artwork_id, n || `Artwork ${d.artwork_id}`);
      }
      return names;
    },
  },

  methods: {
    cancel() {
      this.open = false;
    },

    nameOf(artworkId) {
      return this.nameByArtwork.get(artworkId) || `Artwork ${artworkId}`;
    },

    // product id -> artwork ids, so each call carries only the artworks
    // placed on that product (the endpoint digitizes per product).
    batches() {
      const byProduct = new Map();
      for (const [artworkId, cps] of this.pendingByArtwork) {
        for (const cp of cps) {
          const ids = byProduct.get(cp) || [];
          ids.push(artworkId);
          byProduct.set(cp, ids);
        }
      }
      return byProduct;
    },

    async confirm() {
      if (this.sending || this.blockedReason) return;
      this.open = false;
      this.sending = true;
      this.error = null;
      this.result = null;

      let sentCount = 0;
      const lines = [];
      const handled = new Set();
      const unsent = [];

      // A failed product does NOT stop the loop: product 1's call has already
      // moved its artworks off `uploaded`, so they would drop out of the
      // pending count and a retry could never reach the products skipped here.
      for (const [cp, artworkIds] of this.batches()) {
        try {
          const resp = await axios.post(
            `/api/campaign_products/${cp}/send_pending_artworks`,
            { artwork_ids: artworkIds, client_ids: [Number(this.clientId)] },
            { [SUPPRESS_ERROR_TOAST]: true },
          );
          const body = resp?.data || {};
          sentCount += Number(body.sent_count) || 0;
          const keep = (entry) =>
            !(handled.has(entry.artwork_id) && BENIGN_REPEATS.has(entry.reason));
          for (const s of body.skipped || []) {
            if (keep(s)) {
              lines.push(`${this.nameOf(s.artwork_id)} ${reasonText(s.reason)}.`);
            }
          }
          for (const s of body.digitization_skipped || []) {
            if (keep(s)) {
              lines.push(
                `${this.nameOf(s.artwork_id)}: nothing new to digitize; it ${reasonText(s.reason)}.`,
              );
            }
          }
          for (const s of body.dtf_not_sent || []) {
            const n = Number(s.count) || 1;
            lines.push(
              `${this.nameOf(s.artwork_id)}: ${n} DTF placement${n === 1 ? " was" : "s were"} not sent. DTF art is not sent from here.`,
            );
          }
          for (const id of artworkIds) handled.add(id);
        } catch (err) {
          const msg = err?.response?.data?.error;
          if (!this.error) {
            this.error =
              (typeof msg === "string" && msg.trim()) ||
              "Could not send the pending artworks. Please try again.";
          }
          unsent.push({ cp, artworkIds });
        }
      }

      // Say plainly what was not sent on THIS product and where to resend it.
      // An artwork shared with a product whose call went through was already
      // sent there, so the line is per product, not "NOT sent" overall.
      for (const { cp, artworkIds } of unsent) {
        const names = artworkIds.map((id) => this.nameOf(id)).join(", ");
        lines.push(
          `${this.nameByProduct.get(cp) || `Product ${cp}`}: not sent on this product (${names}). Open each artwork in the Decoration Library and press Send for digitization to resend.`,
        );
      }

      this.sending = false;
      this.result = { sentCount, lines };
      this.$emit("sent");
    },
  },
};
</script>

<style scoped>
.spa { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.spa__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9999px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: var(--tf-primary, #119ae9);
  color: var(--tf-bg, #fff);
}
.spa__btn:disabled { opacity: 0.5; cursor: not-allowed; }
.spa__btn--ghost {
  background: transparent;
  color: var(--tf-fg-2, #424242);
  border: 1px solid var(--tf-border, #e0e0e0);
}
.spa__note { margin: 0; font-size: 12px; color: var(--tf-fg-3, #616161); }
.spa__error { margin: 0; font-size: 13px; color: var(--tf-error, #b3261e); }
.spa__result { font-size: 13px; color: var(--tf-fg-2, #424242); max-width: 520px; }
.spa__result-head { margin: 0 0 4px; font-weight: 500; }
.spa__result-list { margin: 0; padding-left: 18px; }
.spa-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(19, 55, 82, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.spa-dialog {
  background: var(--tf-bg, #fff);
  border-radius: 12px;
  padding: 22px 24px;
  width: 440px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
}
.spa-dialog__title {
  font-family: "Poppins", sans-serif;
  font-size: 17px;
  font-weight: 500;
  color: var(--tf-fg-1, #212121);
  margin: 0 0 8px;
}
.spa-dialog__body {
  font-size: 13px;
  color: var(--tf-fg-3, #616161);
  line-height: 1.55;
  margin: 0 0 14px;
}
.spa-dialog__actions { display: flex; justify-content: flex-end; gap: 8px; }
</style>
